import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import useCatalogueScrollRestore from "@/hooks/useCatalogueScrollRestore";

/**
 * Prints listing page — grouped by category.
 *
 * Three sections are displayed in a fixed order (Illustration → Artwork →
 * Photography). Empty categories are hidden so future ones (e.g. Photography)
 * appear only once product data exists for them.
 *
 * Purchasing UX and grid layout mirror the Greeting Cards page for
 * cross-category consistency. Square `cover.jpg` art is used on this page;
 * the framed 5×7 `preview.jpg` mockup stays on the individual detail page.
 */
const CATEGORY_ORDER = [
  { key: "illustration", label: "Illustration" },
  { key: "art", label: "Artwork" },
  { key: "photography", label: "Photography" },
];

export default function Prints() {
  const [prints, setPrints] = useState([]);

  useEffect(() => {
    api.get("/products?product_type=print&limit=200")
      .then(r => setPrints(r.data))
      .catch(() => {});
  }, []);

  const { saveNow } = useCatalogueScrollRestore(prints.length > 0);

  const grouped = useMemo(() => {
    const byCat = { illustration: [], art: [], photography: [] };
    for (const p of prints) {
      const c = (p.category || "art").toLowerCase();
      (byCat[c] || byCat.art).push(p);
    }
    // Within each category, keep available prints in their original order and
    // push sold-out prints to the bottom — the gallery-style behaviour the
    // studio asked for.
    for (const c of Object.keys(byCat)) {
      byCat[c].sort((a, b) => Number(!!a.sold_out) - Number(!!b.sold_out));
    }
    return byCat;
  }, [prints]);

  return (
    <div data-testid="prints-page" className="fade-up">
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-10">
        <h1 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl text-brand-dark mt-4 leading-[1.1]">
          Prints
        </h1>
        <p className="mt-4 text-brand-neutral max-w-2xl">
          Signed, limited-edition art prints of original Island Studio illustrations, artwork
          and photography — printed on premium archival paper.
        </p>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12">
        <aside
          data-testid="preorder-banner"
          className="border-y border-brand-border bg-[#F4F2EC] py-8 md:py-10 px-6 md:px-12 text-center"
        >
          <p className="text-[11px] tracking-[0.3em] uppercase text-brand-accent font-medium">
            Pre-Order Now Open
          </p>
          <p className="mt-3 text-brand-neutral leading-relaxed max-w-2xl mx-auto">
            All prints are now available for pre-order, with delivery expected in early
            October. Pre-payment secures your order.
          </p>
        </aside>
      </section>

      {prints.length === 0 ? (
        <section className="max-w-[1200px] mx-auto px-6 md:px-12 pt-12 pb-24">
          <p className="text-brand-neutral">Coming soon.</p>
        </section>
      ) : (
        CATEGORY_ORDER.map(({ key, label }) => {
          const items = grouped[key] || [];
          if (items.length === 0) return null;
          return (
            <section
              key={key}
              data-testid={`prints-section-${key}`}
              className="max-w-[1200px] mx-auto px-6 md:px-12 pt-16 md:pt-20 pb-8 last:pb-24"
            >
              <div className="flex items-end justify-between mb-10 border-b border-brand-border pb-4">
                <div>
                  <p className="overline">The Collection</p>
                  <h2 className="font-serif font-light text-2xl md:text-3xl text-brand-dark mt-3 leading-tight">
                    {label}
                  </h2>
                </div>
                <p className="text-sm text-brand-neutral">
                  {items.length} {items.length === 1 ? "print" : "prints"}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-16 gap-x-8 md:gap-x-10 max-w-[1040px] mx-auto">
                {items.map((p) => (
                  <ProductCard
                    key={p.slug}
                    product={p}
                    testIdPrefix="print-card"
                    onNavigate={saveNow}
                  />
                ))}
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}
